import { string, func, bool, instanceOf, oneOf, object } from 'prop-types'
import { Component } from 'react'
import { CheckBox, Conditional, kuiperClassNames } from '@opallabs/kuiper'
import { AnalyticsLayer } from '@opallabs/quanta'
import { DateAndTime } from '../DateAndTime'
import { DateInfoPanel } from '../DateInfoPanel'

import './styles.scss'

class PostScheduler extends Component {
  static propTypes = {
    /**
     * A `Date` representing the `scheduled_at` for the content’s parent
     * moment. Provides a fallback for `startDateTime` and `endDateTime` when
     * rendering UI that requires a date, but it must not be used as an actual
     * value for `startDateTime` or `endDateTime`. For unscheduled moments this
     * is `undefined`.
     */
    contextDateTime: instanceOf(Date),
    dangerValidation: bool,
    /**
     * MomentJS object representing the content's `takedown_scheduled_at` in
     * the moment's `scheduled_at_timezone`.
     *
     * @type {import('moment').Moment | undefined}
     *
     * @deprecated This may not be accurate for the user’s time zone. Use
     *   `endDateTime` with `targetZone`.
     */
    endDate: object,
    /**
     * A `Date` representing the content's `takedown_scheduled_at` timestamp.
     * Always use in combination with `targetZone`.
     */
    endDateTime: instanceOf(Date),
    /**
     * @type {'AM'|'PM'}
     */
    endMeridiem: string,
    /**
     * Computed ISO 8601 time representing the content's
     * `takedown_scheduled_at` time of day, on the date of the content's
     * `takedown_scheduled_at`, in the moment's `scheduled_at_timezone`.
     *
     * @deprecated This is not reliable. Use `endDateTime` with `targetZone`.
     */
    endTime: string,
    /**
     * @type {import('@opallabs/concepts').IETFLanguageTag}
     */
    locale: string,
    onCheckboxToggle: func,
    onEndDateChange: func,
    onScheduledDateChange: func,
    onTimeInputBlur: func,
    /**
     * MomentJS object representing the content's `scheduled_at` in the
     * moment's `scheduled_at_timezone`.
     *
     * @type {import('moment').Moment | undefined}
     *
     * @deprecated This may not be accurate for the user’s time zone. Use
     *   `startDateTime` with `targetZone`.
     */
    scheduledDate: object,
    scheduledMeridiem: oneOf(['am', 'pm']),
    /**
     * Computed ISO 8601 time representing the content's `scheduled_at` time of
     * day, on the date of the content's `scheduled_at`, in the moment's
     * `scheduled_at_timezone`.
     *
     * @deprecated This is not reliable. Use `startDateTime` with `targetZone`.
     */
    scheduledTime: string,
    shouldValidate: bool,
    /**
     * A `Date` representing the content's `scheduled_at` timestamp. Always use
     * in combination with `targetZone`.
     */
    startDateTime: instanceOf(Date),
    /**
     * The IANA time zone name represending the content's
     * `scheduled_at_timezone`.
     *
     * @type {import('@opallabs/concepts').IANATimezoneIdentifier | undefined}
     */
    targetZone: string.isRequired,
    /**
     * @type {import('@opallabs/gem').Theme | undefined}
     *
     * @deprecated Use `<ThemeProvider>` instead.
     */
    theme: string,
    valid: bool,
  }

  constructor(props) {
    super(props)

    this.state = {
      hasEndTimeAndDate: Boolean(this.props.endDate || this.props.endTime),
    }

    this.onCheckboxToggle = this.onCheckboxToggle.bind(this)
  }

  onCheckboxToggle() {
    this.setState((prevState) => ({
      hasEndTimeAndDate: !prevState.hasEndTimeAndDate,
    }))

    this.props.onCheckboxToggle()
  }

  render() {
    /**
     * @type {PostScheduler.propTypes}
     */
    const {
      contextDateTime,
      dangerValidation,
      endDate,
      endDateTime,
      endMeridiem,
      endTime,
      locale,
      onEndDateChange,
      onScheduledDateChange,
      onTimeInputBlur,
      startDateTime,
      scheduledDate,
      scheduledMeridiem,
      scheduledTime,
      shouldValidate,
      targetZone,
      theme,
      valid,
    } = this.props

    // For unscheduled moments just display 'Unscheduled' box and hide toggle
    // for end date.
    const canBeScheduled = Boolean(startDateTime || contextDateTime)

    return (
      <AnalyticsLayer name="Content Composer - Post Scheduler">
        <div className={kuiperClassNames('post-scheduler')}>
          <AnalyticsLayer name="Post Date And Time">
            <DateAndTime
              contextDateTime={contextDateTime}
              dangerValidation={dangerValidation}
              date={scheduledDate}
              dateEditable={true}
              hasTime={canBeScheduled}
              onTimeInputBlur={onTimeInputBlur}
              onChange={onScheduledDateChange}
              meridiem={scheduledMeridiem}
              placeholder="Unscheduled"
              range={true}
              allowableDates={{ start: contextDateTime, end: endDate }}
              realDate={startDateTime}
              shouldValidate={shouldValidate}
              time={scheduledTime}
              valid={valid}
              {...{ locale, targetZone }}
            />
          </AnalyticsLayer>
          <DateInfoPanel {...{ locale, startDateTime, targetZone }} />
          <Conditional condition={canBeScheduled}>
            <AnalyticsLayer name="Post End-Date And Time">
              <CheckBox
                label="This content has an end date and time"
                value={this.state.hasEndTimeAndDate}
                onChange={this.onCheckboxToggle}
                theme={theme}
              />
              <div className={kuiperClassNames('end-date-and-time')}>
                <DateAndTime
                  contextDateTime={startDateTime}
                  label="End Date And Time"
                  date={endDate}
                  onTimeInputBlur={onTimeInputBlur}
                  onChange={onEndDateChange}
                  range={true}
                  allowableDates={{ start: scheduledDate }}
                  meridiem={endMeridiem}
                  realDate={endDateTime}
                  time={endTime}
                  timezoneDisabled={true}
                  {...{ locale, targetZone }}
                />
              </div>
            </AnalyticsLayer>
          </Conditional>
        </div>
      </AnalyticsLayer>
    )
  }
}

export default PostScheduler
